import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload } from 'lucide-react';
import api from '../utils/api';
import BrandingPreview from './BrandingPreview';

const initialForm = {
  slug: '', nomeEmpresa: '', nomeDoSistema: '',
  logo: '', adminUsername: 'admin', adminSenha: '',
};

export default function TenantModal({ isOpen, onClose, tenant = null, initial = null, onSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    if (!isOpen) return;
    if (tenant) {
      setForm({
        slug: tenant.slug, nomeEmpresa: tenant.nomeEmpresa,
        nomeDoSistema: tenant.nomeDoSistema,
        logo: tenant.logo || '', adminUsername: tenant.adminUsername || 'admin', adminSenha: '',
      });
    } else { setForm({ ...initialForm, ...(initial || {}) }); }
    setError('');
  }, [isOpen, tenant, initial]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500_000) { setError('Logo muito grande (máx 500KB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => set('logo', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!tenant) {
      if (!form.slug || !form.nomeEmpresa || !form.nomeDoSistema || !form.adminUsername || !form.adminSenha) {
        setError('Preencha todos os campos obrigatórios.'); return;
      }
      if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(form.slug)) {
        setError('Slug inválido. Use apenas letras minúsculas, números e hífen (3-40 chars).'); return;
      }
      if (form.adminSenha.length < 8) {
        setError('A senha inicial precisa ter pelo menos 8 caracteres.'); return;
      }
    }
    setLoading(true); setError('');
    try {
      if (tenant) {
        await api.put(`/superadmin/tenants/${tenant.slug}`, {
          nomeEmpresa: form.nomeEmpresa, nomeDoSistema: form.nomeDoSistema,
          logo: form.logo,
        });
      } else {
        await api.post('/superadmin/tenants', form);
      }
      onSuccess?.(); onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === 'string') {
        setError(d);
      } else if (Array.isArray(d) && d.length > 0) {
        // Erro de validação do FastAPI (422): lista de {loc, msg, type}
        setError(d.map(item => item.msg).join(' — '));
      } else {
        setError('Erro ao salvar tenant.');
      }
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose} data-testid="tenant-modal-overlay">
      <div className="nexus-modal w-full max-w-4xl flex flex-col"
        style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()} data-testid="tenant-modal">
        <div className="h-1.5 rounded-t-[28px] bg-slate-900" />
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {tenant ? 'Editar Cliente' : 'Novo Cliente'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="tenant-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 p-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 mb-3" data-testid="tenant-modal-error">{error}</div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
            <div className="space-y-3 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="fin-label">Slug *</label>
              <input type="text" value={form.slug} disabled={!!tenant}
                onChange={e => set('slug', e.target.value.toLowerCase())}
                className="fin-input disabled:bg-slate-50 disabled:text-slate-400"
                placeholder="ex: empresa-x" data-testid="tenant-slug-input" />
            </div>
            <div>
              <label className="fin-label">Nome da Empresa *</label>
              <input type="text" value={form.nomeEmpresa}
                onChange={e => set('nomeEmpresa', e.target.value)}
                className="fin-input" data-testid="tenant-empresa-input" />
            </div>
          </div>
          <div>
            <label className="fin-label">Nome do Sistema (exibido ao cliente) *</label>
            <input type="text" value={form.nomeDoSistema}
              onChange={e => set('nomeDoSistema', e.target.value)}
              className="fin-input" placeholder="ex: Financeiro Empresa X" data-testid="tenant-sistema-input" />
          </div>

          <div className="border-t border-slate-100 pt-3 mt-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Identidade Visual</p>
          </div>
          <div>
            <label className="fin-label">Logo (opcional)</label>
            <div className="flex items-center gap-3">
              {form.logo && (
                <img src={form.logo} alt="logo" className="w-12 h-12 rounded-xl border border-slate-200 object-cover" />
              )}
              <button type="button" onClick={() => fileRef.current?.click()}
                className="fin-btn-secondary text-xs"
                data-testid="tenant-logo-upload-btn">
                <Upload size={14} /> {form.logo ? 'Alterar logo' : 'Fazer upload'}
              </button>
              {form.logo && (
                <button type="button" onClick={() => set('logo', '')}
                  className="text-xs text-red-500 hover:underline">Remover</button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
          </div>
          {!tenant && (
            <>
              <div className="border-t border-slate-100 pt-3 mt-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Admin do Cliente</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="fin-label">Usuário Admin *</label>
                  <input type="text" value={form.adminUsername}
                    onChange={e => set('adminUsername', e.target.value)}
                    className="fin-input" data-testid="tenant-admin-username-input" />
                </div>
                <div>
                  <label className="fin-label">Senha Inicial *</label>
                  <input type="password" value={form.adminSenha}
                    onChange={e => set('adminSenha', e.target.value)}
                    className="fin-input" placeholder="Mínimo 8 caracteres" data-testid="tenant-admin-senha-input" />
                </div>
              </div>
            </>
          )}
            </div>
            {/* RIGHT COLUMN — sticky live preview on desktop */}
            <div className="lg:sticky lg:top-0 self-start">
              <BrandingPreview
                nomeDoSistema={form.nomeDoSistema || 'Gastify'}
                nomeEmpresa={form.nomeEmpresa || 'Sua empresa'}
                logo={form.logo}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-100 bg-white rounded-b-[28px]">
          <button onClick={onClose} className="fin-btn-secondary" disabled={loading} data-testid="tenant-modal-cancel">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
            data-testid="tenant-modal-save">
            {loading ? 'Salvando...' : (tenant ? 'Atualizar' : 'Criar Cliente')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
